import { get, post } from '../baseService'
import moment from 'moment'
import type { Moment } from 'moment'
import { CreateCompanyModel } from 'app/models/createCompanyModel'
import { CompanyEditModel } from 'app/models/companyEditModel'
import { CompanyFilterResult } from 'app/models/company/companyFilter'
import { PaymentCompanyModel } from 'app/models/company/paymentCompanyModel'
import { SERVICE_TIME_FORMAT } from 'app/constants/date'


const create = () => {
  const getCompanies = (tenantId?: string, includeDeleted: boolean = false) => {
    const url =
      tenantId == null
        ? `/config-api/company?&includeDeleted=${includeDeleted}`
        : `/config-api/company/${tenantId}?&ignoreMigration=true`
    return get(url)
  }
  const getCompaniesSimple = (
    tenantId?: string,
    includeDeleted: boolean = false,
    includeChildren: boolean = false,
  ) => {
    // includeChildren nests each company's child companies (for a konto uslugowe: its
    // scope companies) under childCompanies, which constructCompanies() flattens.
    const children = includeChildren ? '&includeChildren=true' : ''
    const url =
      tenantId == null
        ? `/config-api/company/companiesSimple?&includeDeleted=${includeDeleted}${children}`
        : `/config-api/company/companiesSimple?&ignoreMigration=true&tenantId=${tenantId}${children}`
    return get(url)
  }
  const getCompaniesSimpleForGroups = (
    ids: Array<string>,
    includeDeleted: boolean = false,
  ) => {
    return post(
      '/config-api/company/companiesForGroups',
      {
        ids: ids,
        includeDeleted: includeDeleted,
      },
      true,
    )
  }

  const getPaymentCompanies = (
    source: number,
    exported: boolean,
    notExported: boolean,
  ) => {
    const url = `/config-api/company/paymentCompanies?&source=${source}&exported=${exported}&notExported=${notExported}`
    return get(url)
  }

  const getPackageHistory = (companyId: string) => {
    const url = `/config-api/company/packageHistory?&companyId=${companyId}`
    return get(url)
  }

  const getFilteredCompanies = (
    tenantId?: string,
    take?: number,
    skip?: number,
    orderBy?: string,
    descending?: boolean,
    filters?: CompanyFilterResult,
    includeDeleted: boolean = false,
  ) => {
    return post(
      '/config-api/company/listFiltered',
      {
        paged: true,
        includeDeleted: false,
        tenantId: tenantId,
        take: take,
        skip: skip,
        orderBy: orderBy,
        descending: descending,
        filters: filters,
      },
      true,
    )
  }

  const getDemoCompanies = (
    tenantId?: string,
    take?: number,
    skip?: number,
    orderBy?: string,
    descending?: boolean,
    filters?: CompanyFilterResult,
    includeDeleted: boolean = false,
  ) => {
    return post(
      '/config-api/company/demo',
      {
        paged: true,
        includeDeleted: false,
        tenantId: tenantId,
        take: take,
        skip: skip,
        orderBy: orderBy,
        descending: descending,
        filters: filters,
      },
      true,
    )
  }

  const getActiveDrivers = (companyId?: string) => {
    const url = `/config-api/company/${companyId}/activeDrivers`
    return get(url)
  }
  const getCurrentCompany = (companyId: string) =>
    get(`api/${companyId}/company`)
  const getWpfCompanies = (companyId: string, serverId: string) => {
    const url =
      companyId != null
        ? `/api/${serverId}/wpfcompany/?&companyId=${companyId}&serverId=${serverId}`
        : `/api/${serverId}/wpfcompany/?&serverId=${serverId}`
    return get(url, false)
  }

  const importFromGus = (nip: string) =>
    get(`/config-api/company/importFromGus/?&nip=${nip}`)

  const getCompany = (companyId: string, ignoreMigration: boolean) =>
    get(
      `/config-api/company/${companyId}?&ignoreMigration=${ignoreMigration}`,
    )
  const addCompany = (companyInput: CreateCompanyModel) =>
    post('/config-api/company/create', companyInput, false)

  const editPaymentCompany = (company: PaymentCompanyModel, force: boolean) =>
    post(
      '/config-api/company/savePaymentCompany',
      {
        Company: {
          ...company,
          suspensionDate:
            company.suspensionDate != null
              ? moment(company.suspensionDate).format(SERVICE_TIME_FORMAT)
              : null,
          suspendedFrom:
            company.suspendedFrom != null
              ? moment(company.suspendedFrom).format(SERVICE_TIME_FORMAT)
              : null,
          suspendedTo:
            company.suspendedTo != null
              ? moment(company.suspendedTo).format(SERVICE_TIME_FORMAT)
              : null,
        },
        force,
      },
      false,
    )

  const editDemoCompany = (
    companyId: string,
    dateTo: Moment,
    name: string,
  ) =>
    post(
      '/config-api/company/editDemoCompany',
      {
        companyId: companyId,
        dateTo: moment(dateTo).format(SERVICE_TIME_FORMAT),
        name: name,
      },
      false,
    )

  const changeCompany = (companyId: string) =>
    post('/config-api/company/change', { id: companyId }, false)
  const editCompany = (company: CompanyEditModel) =>
    post('/config-api/company/edit', company, false)
  const deleteCompany = (ids: string[]) =>
    Promise.all(
      ids.map((id) =>
        post('/config-api/company/delete', { id }, false),
      ),
    )

  const uploadFile = (file: any) =>
    post(
      `/config-api/company/fileUpload`,
      file,
      false,
      'multipart/form-data',
    )

  const resetPaymentState = (companyId: string) => {
    return post(
      '/config-api/company/resetPaymentState',
      { companyId },
      true,
    )
  }

  const activateCompany = (companyIds: string[], isActive: boolean) =>
    post(
      '/config-api/company/activate',
      {
        companyIds: companyIds.map(id => parseInt(id)),
        isActive: isActive,
      },
      true,
    )

  return {
    getCompanies,
    getCompany,
    getActiveDrivers,
    addCompany,
    editCompany,
    deleteCompany,
    changeCompany,
    getCurrentCompany,
    getWpfCompanies,
    getFilteredCompanies,
    getCompaniesSimple,
    importFromGus,
    getPaymentCompanies,
    editPaymentCompany,
    uploadFile,
    getPackageHistory,
    getDemoCompanies,
    editDemoCompany,
    resetPaymentState,
    getCompaniesSimpleForGroups,
    activateCompany,
  }
}

export default {
  create,
}
